import { Fraction } from './fraction'
import { SimplexSolver, SimplexTableau } from './simplex'
import { generateCut, CGFType } from './cgf'
import { findIntegerPointsInPolyhedron, computeAABBFromInequalities } from '../graphics/geometry'
import { removeRedundantConstraints } from './constraint-processing'
import { STABILITY } from '@/app/config'
import { logger } from '@/app/logger'
import type { RationalProblem } from './rational'

/**
 * Cut generation and translation utilities
 * Handles conversion between standard form and canonical form cuts
 */

/**
 * Integer constraint representation
 */
export interface Inequality {
  alpha: number[];
  beta: number;
}

export interface IntegerProblem {
  A: number[][];      // Constraint matrix (integers)
  b: number[];        // RHS vector (integers)
  c: number[];        // Objective coefficients
  inequalities: Inequality[]; // The original inequalities
  // Optional: original rational problem before integer normalization, if available.
  fracData?: RationalProblem;
}

export type CutOrigin = CGFType | 'user'

/**
 * Generated cut in canonical form
 */
export interface CanonicalCut {
  alpha: number[]  // Cut coefficients for original variables
  beta: number     // Cut RHS
  efficacy: number // Cut strength measure
  sourceRow: number // Primary tableau row index that generated this cut
  cgfType: CutOrigin // Which CGF was used
  // Extra metadata (optional)
  secondSourceRow?: number        // Secondary row index (for 2D-Simplex)
  bestMu?: number                 // Optimal μ in (0,1) for 2D-Simplex
  fVals?: number[]                // Fractional RHS values for rows (2D-Simplex)
  rays?: number[][]               // Ray vectors for rows (2D-Simplex)
  userAdjustable?: boolean        // Whether this cut supports user μ adjustment
}

/**
 * Result of cut generation process
 */
export interface CutGenerationResult {
  cuts: CanonicalCut[]
  tableau: SimplexTableau
  fractionalRows: number[]
  cleanedProblem: IntegerProblem
}

/**
 * Ensure that A and b contain only integers
 */
export function ensureIntegerMatrix(A: number[][], b: number[]): { A_int: number[][], b_int: number[] } {
  // Validate A
  const A_int: number[][] = []
  for (let i = 0; i < A.length; i++) {
    const row: number[] = []
    for (let j = 0; j < A[i].length; j++) {
      const val = Math.round(A[i][j])
      if (Math.abs(val - A[i][j]) > 1e-10) {
        throw new Error(`A[${i}][${j}] = ${A[i][j]} is not an integer`)
      }
      row.push(val)
    }
    A_int.push(row)
  }
  
  // Validate b
  const b_int: number[] = []
  for (let i = 0; i < b.length; i++) {
    const val = Math.round(b[i])
    if (Math.abs(val - b[i]) > 1e-10) {
      throw new Error(`b[${i}] = ${b[i]} is not an integer`)
    }
    b_int.push(val)
  }
  
  return { A_int, b_int }
}

/**
 * Construct standard form by adding slack variables
 */
function constructStandardForm(A_int: number[][], _b_int: number[], c: number[]): {
  A_eq: number[][],
  c_eq: number[]
} {
  const m = A_int.length
  
  // Add slack variables
  const A_eq: number[][] = []
  for (let i = 0; i < m; i++) {
    const row = [...A_int[i]] // Copy original row
    
    // Add slack variables (identity matrix)
    for (let j = 0; j < m; j++) {
      row.push(i === j ? 1 : 0)
    }
    
    A_eq.push(row)
  }
  
  // Extend objective with zeros for slack variables
  const c_eq = [...c, ...new Array(m).fill(0)]
  
  return { A_eq, c_eq }
}

/**
 * Get candidate rows from optimal tableau
 * A candidate row must be:
 * 1. A basic variable row (in the basis)
 * 2. Have fractional RHS value
 */
export function getCandidateTableauRows(A_int: number[][], b_int: number[], c: number[]): {
  candidateRows: Fraction[][]
  tableau: SimplexTableau
  candidateRowIndices: number[]
  basicVariableInfo: { rowIndex: number, varIndex: number, isFractional: boolean }[]
  cleanedProblem: IntegerProblem
} {
  // First, remove redundant constraints to avoid unnecessary candidate rows
  const problem: IntegerProblem = {
    A: A_int,
    b: b_int,
    c: c,
    inequalities: A_int.map((row, i) => ({ alpha: row, beta: b_int[i] }))
  }
  
  const { cleanedProblem } = removeRedundantConstraints(problem)
  
  logger.log(`=== Redundancy Filtering ===`)
  logger.log(`Original constraints: ${A_int.length}`)
  logger.log(`After redundancy removal: ${cleanedProblem.A.length}`)
  logger.log(`================================`)
  
  // IMPORTANT: Do NOT pre-augment with slack variables here.
  // The simplex solver constructs the standard-form tableau by adding a single
  // set of slack variables internally. Pre-augmenting here would duplicate
  // slack variables and corrupt the tableau shape (n + 2m columns), which
  // then breaks cut translation assumptions (expecting n + m coefficients).
  // See Python reference: standard form is applied once before computing B^{-1}.
  // Therefore we pass the original A, b, c directly.
  let tableau = SimplexSolver.getOptimalTableau(cleanedProblem.A, cleanedProblem.b, c)
  
  if (!tableau.isOptimal) {
    throw new Error('Failed to find optimal solution')
  }

  // Proactive numerical stabilization: simplify large fractions in the tableau
  const MAX_COEFFICIENT_VAL = STABILITY.tableauCoeffLimit
  let stabilizationApplied = false;
  for (let i = 0; i < tableau.tableau.length; i++) {
    for (let j = 0; j < tableau.tableau[i].length; j++) {
      const frac = tableau.tableau[i][j];
      if (frac.isNumericallyUnstable(MAX_COEFFICIENT_VAL)) {
        tableau.tableau[i][j] = Fraction.createStable(frac, MAX_COEFFICIENT_VAL);
        stabilizationApplied = true;
      }
    }
  }

  if (stabilizationApplied) {
    logger.warn(`[Numerical Stabilization] Simplified oversized fractions in the optimal tableau to maintain stability.`)
  }
  
  let candidateRows: Fraction[][] = []
  let candidateRowIndices: number[] = []
  let basicVariableInfo: { rowIndex: number, varIndex: number, isFractional: boolean }[] = []
  
  logger.log('=== Candidate Row Analysis ===')
  logger.log('Basic variables:', tableau.basicVars)
  logger.log('Non-basic variables:', tableau.nonBasicVars)
  
  // Expected tableau row width (excluding RHS): n original vars + m slack vars
  const expectedRayLength = (cleanedProblem.A[0]?.length || 0) + cleanedProblem.A.length

  const scanTableauForCandidates = () => {
    candidateRows = []
    candidateRowIndices = []
    basicVariableInfo = []

    // Proactive numerical stabilization: simplify large fractions in the tableau (again, in case tableau changed)
    const MAX_COEFFICIENT_VAL = STABILITY.tableauCoeffLimit
    let stabilizationAppliedLocal = false
    for (let i = 0; i < tableau.tableau.length; i++) {
      for (let j = 0; j < tableau.tableau[i].length; j++) {
        const frac = tableau.tableau[i][j]
        if (frac.isNumericallyUnstable(MAX_COEFFICIENT_VAL)) {
          tableau.tableau[i][j] = Fraction.createStable(frac, MAX_COEFFICIENT_VAL)
          stabilizationAppliedLocal = true
        }
      }
    }
    if (stabilizationAppliedLocal) {
      logger.warn(`[Numerical Stabilization] (Rescan) Simplified oversized fractions in the optimal tableau.`)
    }

    // Check each constraint row (exclude objective row)
    for (let i = 0; i < tableau.tableau.length - 1; i++) {
      const row = tableau.tableau[i]
      // Validate tableau shape to ensure we didn't accidentally add slack twice
      if (row.length - 1 !== expectedRayLength) {
        throw new Error(
          `Unexpected tableau row width: got ${row.length - 1}, expected ${expectedRayLength}. ` +
          `This usually indicates duplicate slack variables were added. ` +
          `Ensure getCandidateTableauRows passes raw A,b,c to the simplex solver.`
        )
      }
      const rhs = row[row.length - 1] // Last element is RHS
      const rhsFractionalPart = rhs.fractionalPart()
      const isFractional = !rhsFractionalPart.isZero()

      // Get the basic variable for this row
      const basicVarIndex = tableau.basicVars[i]

      basicVariableInfo.push({
        rowIndex: i,
        varIndex: basicVarIndex,
        isFractional
      })

      logger.log(`Row ${i}: Basic var x${basicVarIndex}, RHS = ${rhs.toString()}, Fractional = ${isFractional}`)

      // A row is a candidate if it's a basic variable row with fractional RHS
      if (isFractional) {
        // Store the entire row without RHS for cut generation
        candidateRows.push(row.slice(0, -1)) // Exclude RHS for CGF input
        candidateRowIndices.push(i)
        logger.log(`  → Added as candidate row`)
      } else {
        logger.log(`  → Skipped (integer RHS)`)
      }
    }

    logger.log(`Found ${candidateRowIndices.length} candidate rows from ${tableau.tableau.length - 1} total rows`)
    logger.log('================================')
  }

  // Initial scan
  scanTableauForCandidates()

  // Degeneracy handling: if no candidate row is found but LP solution has fractional components,
  // perturb objective slightly to obtain an alternative optimal basis that exposes fractional RHS.
  if (candidateRowIndices.length === 0) {
    try {
      const n = cleanedProblem.A[0]?.length || 0
      const xLP = tableau.optimalSolution.slice(0, n).map((f) => f.toNumber())
      const hasFractionalComponent = xLP.some((v) => Math.abs(v - Math.round(v)) > 1e-10)

      if (hasFractionalComponent) {
        logger.warn('[Degeneracy] No fractional RHS rows but LP solution has fractional components. Applying tiny objective perturbation to break degeneracy...')
        const EPS = 1e-6
        const cPerturbed = c.map((ci, i) => ci + EPS * (i + 1))

        // Resolve with perturbed objective and rescan
        tableau = SimplexSolver.getOptimalTableau(cleanedProblem.A, cleanedProblem.b, cPerturbed)
        scanTableauForCandidates()

        if (candidateRowIndices.length === 0) {
          logger.warn('[Degeneracy] Perturbation did not expose fractional RHS rows; proceeding without candidates.')
        } else {
          logger.log(`[Degeneracy] Perturbation successful: found ${candidateRowIndices.length} candidate row(s).`)
        }
      }
    } catch (e) {
      logger.warn('[Degeneracy] Objective perturbation failed:', e as any)
    }
  }
  
  return { candidateRows, tableau, candidateRowIndices, basicVariableInfo, cleanedProblem }
}

/**
 * Translate a cut from standard form to canonical form
 * 
 * Standard form cut: sum(cut_coeffs[j] * x_j) >= 1 where x includes slack variables
 * Canonical form cut: sum(alpha[j] * x_orig[j]) <= beta where x_orig are original variables
 * 
 * The transformation is:
 * alpha = A^T * a_s - a_x
 * beta = b^T * a_s - 1
 * 
 * where a_x are coefficients for original variables and a_s are coefficients for slack variables
 */
export function translateCutToCanonicalForm(
  A_int: number[][], 
  b_int: number[], 
  cutCoeffs: Fraction[]
): { alpha: number[], beta: number } {
  const m = A_int.length
  const n = A_int.length > 0 ? A_int[0].length : 0
  
  // Convert to fractions for exact computation
  const A_frac = A_int.map(row => row.map(val => new Fraction(val)))
  const b_frac = b_int.map(val => new Fraction(val))
  
  // The cut coefficients correspond to all variables in the tableau
  // We need to identify which correspond to original variables and which to slack variables
  // Based on the tableau structure: [original vars][slack vars][additional vars if any]
  
  if (cutCoeffs.length < n + m) {
    throw new Error(`Cut coefficients length ${cutCoeffs.length} is less than expected ${n + m}`)
  }
  
  // Take first n coefficients as original variables
  const a_x = cutCoeffs.slice(0, n)  // Original variables
  
  // Take next m coefficients as slack variables
  const a_s = cutCoeffs.slice(n, n + m)  // Slack variables
  
  // Compute alpha = A^T @ a_s - a_x
  const alpha_frac: Fraction[] = []
  for (let j = 0; j < n; j++) {
    let val = Fraction.zero()
    for (let i = 0; i < m; i++) {
      val = val.add(A_frac[i][j].multiply(a_s[i]))
    }
    val = val.subtract(a_x[j])
    alpha_frac.push(val)
  }
  
  // Compute beta = b^T @ a_s - 1
  let beta_frac = Fraction.zero()
  for (let i = 0; i < m; i++) {
    beta_frac = beta_frac.add(b_frac[i].multiply(a_s[i]))
  }
  beta_frac = beta_frac.subtract(Fraction.one())
  
  // Convert to simplified integers
  return simplifyToIntegers(alpha_frac, beta_frac)
}

/**
 * Translate a cut from standard form to canonical form with numerical stability
 * 
 * This is the mixed-precision version that handles numerically unstable fractions
 * by converting them to controlled-precision floating point when needed
 */
export function translateCutToCanonicalFormStable(
  A_int: number[][], 
  b_int: number[], 
  cutCoeffs: Fraction[]
): { alpha: number[], beta: number } {
  const m = A_int.length
  const n = A_int.length > 0 ? A_int[0].length : 0
  
  // Check if any coefficient is numerically unstable
  const hasUnstableCoeffs = cutCoeffs.some(coeff => coeff.isNumericallyUnstable())
  
  if (hasUnstableCoeffs) {
    logger.log('🔧 [Mixed Precision] Detected numerically unstable coefficients, using float precision')
    
    // Convert coefficients to stable decimals
    const stableCoeffs = cutCoeffs.map(coeff => coeff.toStableDecimal(STABILITY.stableDecimalPrecision))
    
    // Use floating point arithmetic for the translation
    if (stableCoeffs.length < n + m) {
      throw new Error(`Cut coefficients length ${stableCoeffs.length} is less than expected ${n + m}`)
    }
    
    const a_x = stableCoeffs.slice(0, n)
    const a_s = stableCoeffs.slice(n, n + m)
    
    // Compute alpha = A^T @ a_s - a_x using floating point
    const alpha_float: number[] = []
    for (let j = 0; j < n; j++) {
      let val = 0
      for (let i = 0; i < m; i++) {
        val += A_int[i][j] * a_s[i]
      }
      val -= a_x[j]
      alpha_float.push(val)
    }
    
    // Compute beta = b^T @ a_s - 1 using floating point
    let beta_float = 0
    for (let i = 0; i < m; i++) {
      beta_float += b_int[i] * a_s[i]
    }
    beta_float -= 1
    
    // Convert to simplified integers using controlled precision
    return simplifyToIntegersStable(alpha_float, beta_float)
  } else {
    logger.log('🔧 [Mixed Precision] Using exact fraction arithmetic (coefficients are stable)')
    // Use the original exact method for stable coefficients
    return translateCutToCanonicalForm(A_int, b_int, cutCoeffs)
  }
}

/**
 * Convert fractional coefficients to simplified integer form
 */
function simplifyToIntegers(alpha_frac: Fraction[], beta_frac: Fraction): { alpha: number[], beta: number } {
  // Find least common multiple of all denominators
  const allDenominators = alpha_frac.map(f => Number(f.getDenominator())).concat([Number(beta_frac.getDenominator())])
  const commonDenom = allDenominators.reduce((lcm, denom) => lcm * denom / gcd(lcm, denom), 1)
  
  // Convert to integers
  const alpha_int = alpha_frac.map(f => {
    const num = Number(f.getNumerator()) * commonDenom / Number(f.getDenominator())
    return Math.round(num)
  })
  
  const beta_int = Math.round(Number(beta_frac.getNumerator()) * commonDenom / Number(beta_frac.getDenominator()))
  
  // Simplify by GCD
  const allCoeffs = [...alpha_int, beta_int]
  const nonzeroCoeffs = allCoeffs.filter(x => x !== 0).map(x => Math.abs(x))
  
  if (nonzeroCoeffs.length > 0) {
    const overallGcd = nonzeroCoeffs.reduce((g, x) => gcd(g, x))
    if (overallGcd > 1) {
      const alpha = alpha_int.map(x => Math.round(x / overallGcd))
      const beta = Math.round(beta_int / overallGcd)
      return { alpha, beta }
    }
  }
  
  return { alpha: alpha_int, beta: beta_int }
}

/**
 * Convert floating point coefficients to simplified integer form with controlled precision
 */
function simplifyToIntegersStable(alpha_float: number[], beta_float: number): { alpha: number[], beta: number } {
  // Use a reasonable scaling factor to convert to integers
  const maxCoeff = Math.max(...alpha_float.map(Math.abs), Math.abs(beta_float))
  
  if (maxCoeff === 0) {
    return { alpha: alpha_float.map(() => 0), beta: 0 }
  }
  
  // Choose scale factor to get reasonable integer coefficients
  const LIMIT = STABILITY.cutCoeffLimit
  const scale = Math.min(LIMIT, Math.max(1, LIMIT / maxCoeff))
  
  // Convert to integers
  const alpha_int = alpha_float.map(x => Math.round(x * scale))
  const beta_int = Math.round(beta_float * scale)
  
  // Simplify by GCD
  const allCoeffs = [...alpha_int, beta_int]
  const nonzeroCoeffs = allCoeffs.filter(x => x !== 0).map(x => Math.abs(x))
  
  if (nonzeroCoeffs.length > 0) {
    const overallGcd = nonzeroCoeffs.reduce((g, x) => gcd(g, x))
    if (overallGcd > 1) {
      return {
        alpha: alpha_int.map(x => Math.round(x / overallGcd)),
        beta: Math.round(beta_int / overallGcd)
      }
    }
  }
  
  return { alpha: alpha_int, beta: beta_int }
}

/**
 * Greatest Common Divisor
 */
function gcd(a: number, b: number): number {
  a = Math.abs(a)
  b = Math.abs(b)
  while (b !== 0) {
    const temp = b
    b = a % b
    a = temp
  }
  return a
}

/**
 * Generate a single cut from a tableau row using specified CGF
 */
export function generateSingleCut(
  problem: IntegerProblem,
  rowIndex: number,
  cgfType: CGFType,
  secondRow?: number,
  optimalSolution?: number[]
): CanonicalCut {
  const { A_int, b_int } = ensureIntegerMatrix(problem.A, problem.b)
  const { candidateRows, tableau, candidateRowIndices, cleanedProblem } = getCandidateTableauRows(A_int, b_int, problem.c)
  
  if (!candidateRowIndices.includes(rowIndex)) {
    throw new Error(`Row ${rowIndex} does not have fractional RHS`)
  }
  
  const rowIdx = candidateRowIndices.indexOf(rowIndex)
  const tableauRow = tableau.tableau[rowIndex]
  const rhs = tableauRow[tableauRow.length - 1]
  const ray = candidateRows[rowIdx]
  
  let cutCoeffs: Fraction[]
  let bestMuValue: Fraction | null = null
  let fValsNumbers: number[] | undefined
  let raysNumbers: number[][] | undefined
  
  if (cgfType === '2dsimplex') {
    if (secondRow === undefined) {
      throw new Error('2D-Simplex requires second row index')
    }
    
    if (!candidateRowIndices.includes(secondRow)) {
      throw new Error(`Second row ${secondRow} does not have fractional RHS`)
    }
    
    const secondRowIdx = candidateRowIndices.indexOf(secondRow)
    const secondTableauRow = tableau.tableau[secondRow]
    const secondRhs = secondTableauRow[secondTableauRow.length - 1]
    const secondRay = candidateRows[secondRowIdx]
    
    const f = [rhs, secondRhs]
    const rays = [ray, secondRay]
    
    const result = generateCut(cgfType, f, rays, { showBestMu: true })
    if (Array.isArray(result)) {
      cutCoeffs = result
    } else {
      cutCoeffs = result.coefficients
      bestMuValue = result.optimalMu
    }

    // Store f and rays numeric values for later usage
    fValsNumbers = [rhs.toNumber(), secondRhs.toNumber()]
    raysNumbers = [ray.map((val: Fraction) => val.toNumber()), secondRay.map((val: Fraction) => val.toNumber())]
  } else {
    const result = generateCut(cgfType, rhs, [ray])
    cutCoeffs = Array.isArray(result) ? result : result.coefficients
  }
  
  // Translate to canonical form using numerically stable method
  const { alpha, beta } = translateCutToCanonicalFormStable(cleanedProblem.A, cleanedProblem.b, cutCoeffs)
  
  // Compute efficacy using correct formula: |α^T x_LP - β| / ||α||_2
  let efficacy = 0
  
  // Get optimal solution if not provided
  const x_LP = optimalSolution || tableau.optimalSolution.slice(0, problem.c.length).map(frac => frac.toNumber())
  
  // Calculate α^T x_LP
  let alphaTransposeX_LP = 0
  for (let i = 0; i < alpha.length; i++) {
    alphaTransposeX_LP += alpha[i] * x_LP[i]
  }
  
  // Calculate ||α||_2 (L2 norm)
  const l2NormSquared = alpha.reduce((sum, coeff) => sum + coeff * coeff, 0)
  const l2Norm = Math.sqrt(l2NormSquared)
  
  // Calculate efficacy: |α^T x_LP - β| / ||α||_2
  if (l2Norm > 0) {
    efficacy = Math.abs(alphaTransposeX_LP - beta) / l2Norm
  }
  
  const baseCut: CanonicalCut = {
    alpha,
    beta,
    efficacy,
    sourceRow: rowIndex,
    cgfType
  }

  if (cgfType === '2dsimplex') {
    Object.assign(baseCut, {
      secondSourceRow: secondRow,
      bestMu: bestMuValue ? bestMuValue.toNumber() : undefined,
      fVals: fValsNumbers,
      rays: raysNumbers,
      userAdjustable: true  // All 2D-Simplex cuts support user μ adjustment
    })
  }

  return baseCut
}



/**
 * Remove duplicate cuts across all types (cross-type deduplication)
 * Uses normalized cut representation as key and keeps highest efficacy cut
 */
function removeDuplicateCuts(cuts: CanonicalCut[]): CanonicalCut[] {
  logger.log('Starting cross-type deduplication...')
  
  // Map from normalized cut key to best cut
  const uniqueCutsMap = new Map<string, CanonicalCut>()
  
  for (const cut of cuts) {
    // Create normalized key: convert to minimal integer representation
    const normalizedKey = createNormalizedCutKey(cut.alpha, cut.beta)
    
    const existingCut = uniqueCutsMap.get(normalizedKey)
    
    if (!existingCut) {
      // First cut with this key
      uniqueCutsMap.set(normalizedKey, cut)
      logger.log(`Added new unique cut (${cut.cgfType}): key=${normalizedKey}, efficacy=${cut.efficacy.toFixed(4)}`)
    } else {
      // Duplicate found - keep the one with higher efficacy
      if (cut.efficacy > existingCut.efficacy) {
        uniqueCutsMap.set(normalizedKey, cut)
        logger.log(`Replaced duplicate cut: ${existingCut.cgfType}(${existingCut.efficacy.toFixed(4)}) → ${cut.cgfType}(${cut.efficacy.toFixed(4)})`)
      } else {
        logger.log(`Skipped duplicate cut: ${cut.cgfType}(${cut.efficacy.toFixed(4)}) ≤ ${existingCut.cgfType}(${existingCut.efficacy.toFixed(4)})`)
      }
    }
  }
  
  // Convert map values to array and sort by efficacy
  const uniqueCuts = Array.from(uniqueCutsMap.values())
  uniqueCuts.sort((a, b) => b.efficacy - a.efficacy)
  
  logger.log(`Total unique cuts after cross-type deduplication: ${uniqueCuts.length}`)
  
  // Log final statistics by type
  const cutsByType = {
    'gmi': uniqueCuts.filter(cut => cut.cgfType === 'gmi').length,
    'gomory': uniqueCuts.filter(cut => cut.cgfType === 'gomory').length,
    '2dsimplex': uniqueCuts.filter(cut => cut.cgfType === '2dsimplex').length
  }
  logger.log('Final cuts by type:', cutsByType)
  
  return uniqueCuts
}

/**
 * Create a normalized key for a cut by converting to minimal integer form
 * This ensures that proportional cuts (same hyperplane) get the same key
 */
function createNormalizedCutKey(alpha: number[], beta: number, tolerance: number = 1e-10): string {
  // Find the GCD of all coefficients to reduce to minimal integer form
  const allCoeffs = [...alpha, beta].map(x => Math.abs(Math.round(x * 1000000))) // Scale and round to handle floating point
  const coeffGcd = allCoeffs.reduce((acc, val) => gcd(acc, val), allCoeffs[0] || 1)
  
  // Normalize by dividing by GCD
  const normalizedAlpha = alpha.map(x => Math.round(x * 1000000 / coeffGcd))
  const normalizedBeta = Math.round(beta * 1000000 / coeffGcd)
  
  // Ensure consistent sign (make first non-zero coefficient positive)
  let signMultiplier = 1
  for (const coeff of normalizedAlpha) {
    if (Math.abs(coeff) > tolerance) {
      if (coeff < 0) {
        signMultiplier = -1
      }
      break
    }
  }
  
  const finalAlpha = normalizedAlpha.map(x => x * signMultiplier)
  const finalBeta = normalizedBeta * signMultiplier
  
  return `[${finalAlpha.join(',')}]<=${finalBeta}`
}

/**
 * Generate all possible cuts from fractional tableau rows
 */
export function generateAllCuts(problem: IntegerProblem, cgfTypes: CGFType[] = ['gmi', 'gomory']): CutGenerationResult {
  const { A_int, b_int } = ensureIntegerMatrix(problem.A, problem.b)
  const { tableau, candidateRowIndices, cleanedProblem } = getCandidateTableauRows(A_int, b_int, problem.c)
  
  // Extract optimal solution for efficacy calculation
  const optimalSolution = tableau.optimalSolution.slice(0, problem.c.length).map((frac: Fraction) => frac.toNumber())
  
  const cuts: CanonicalCut[] = []
  
  logger.log(`Generating cuts from ${candidateRowIndices.length} candidate rows using CGF types: ${cgfTypes.join(', ')}`)
  
  // Generate single-row cuts
  for (const cgfType of cgfTypes) {
    logger.log(`Generating ${cgfType} cuts...`)
    let generatedCount = 0
    
    if (cgfType === '2dsimplex') {
      // Generate exactly C(m,2) cuts where m is the number of candidate rows
      const m = candidateRowIndices.length
      logger.log(`Attempting to generate 2D-Simplex cuts from ${m} candidate rows`)
      
      if (m < 2) {
        logger.warn(`Cannot generate 2D-Simplex cuts: need at least 2 candidate rows, but only have ${m}`)
      } else {
        const expectedCuts = (m * (m - 1)) / 2 // C(m,2) = m!/(2!(m-2)!) = m*(m-1)/2
        logger.log(`Will generate exactly ${expectedCuts} 2D-Simplex cuts from C(${m},2) combinations`)
        
        // Generate all C(m,2) combinations without duplication
        for (let i = 0; i < m; i++) {
          for (let j = i + 1; j < m; j++) {
            const row1 = candidateRowIndices[i]
            const row2 = candidateRowIndices[j]
            
            try {
              logger.log(`Generating 2D-Simplex cut from candidate rows ${row1} and ${row2} (combination ${generatedCount + 1}/${expectedCuts})`)
              const cut = generateSingleCut(problem, row1, cgfType, row2, optimalSolution)
              cuts.push(cut)
              generatedCount++
              logger.log(`Successfully generated 2D-Simplex cut with efficacy ${cut.efficacy.toFixed(4)}`)
            } catch (error) {
              // Log error but continue - we expect exactly C(m,2) attempts
              logger.warn(`Failed to generate 2D-Simplex cut for candidate rows ${row1}, ${row2}: ${error instanceof Error ? error.message : String(error)}`)
            }
          }
        }
        
        logger.log(`2D-Simplex generation complete: ${generatedCount}/${expectedCuts} cuts successfully generated`)
      }
    } else {
      // Generate single-row cuts
      for (const rowIndex of candidateRowIndices) {
        try {
          const cut = generateSingleCut(problem, rowIndex, cgfType, undefined, optimalSolution)
          cuts.push(cut)
          generatedCount++
        } catch (error) {
          // Skip invalid cuts
          logger.warn(`Failed to generate ${cgfType} cut for row ${rowIndex}: ${error instanceof Error ? error.message : String(error)}`)
        }
      }
    }
    
    logger.log(`Generated ${generatedCount} ${cgfType} cuts`)
  }
  
  logger.log(`Total cuts before deduplication: ${cuts.length}`)

  // Heuristic CG rounding from original constraints (degeneracy-friendly)
  // If constraints share a common factor g>1 but RHS not divisible by g, we can
  // use u = 1/g so that uA is integral and add the valid CG cut:
  //   (A/g) x <= floor(b/g)
  // This is particularly useful for equalities like 2x+2y+2z = 3 where
  // the ≤ side yields x+y+z <= floor(3/2) = 1.
  try {
    const roundingCuts: CanonicalCut[] = []
    for (let i = 0; i < cleanedProblem.A.length; i++) {
      const row = cleanedProblem.A[i]
      const beta = cleanedProblem.b[i]
      // Prefer rows with non-negative RHS to avoid producing "≥"-type effects
      if (beta <= 0) continue
      const nonZero = row.filter((v) => v !== 0).map((v) => Math.abs(v))
      if (nonZero.length === 0) continue
      const g = nonZero.reduce((acc, v) => gcd(acc, v))
      if (g > 1 && (Math.abs(beta) % g !== 0)) {
        // Candidate rounding cut
        const alphaR = row.map((v) => Math.trunc(v / g))
        const betaR = Math.floor(beta / g)
        // Compute efficacy at current LP solution
        let lhs = 0
        for (let k = 0; k < alphaR.length && k < optimalSolution.length; k++) lhs += alphaR[k] * optimalSolution[k]
        const l2 = Math.sqrt(alphaR.reduce((s, a) => s + a * a, 0)) || 1
        const eff = Math.abs(lhs - betaR) / l2
        // Keep only if actually cuts off current LP (violation > 0)
        if (lhs - betaR > 1e-10) {
          roundingCuts.push({ alpha: alphaR, beta: betaR, efficacy: eff, sourceRow: i, cgfType: 'gomory' })
        }
      }
    }
    if (roundingCuts.length > 0) {
      logger.log(`[CG Rounding] Added ${roundingCuts.length} degeneracy-friendly rounding cut(s) from original constraints.`)
      cuts.push(...roundingCuts)
    } else if (candidateRowIndices.length === 0 && cuts.length === 0) {
      logger.log('[CG Rounding] No rounding cuts derivable from original constraints.')
    }
  } catch (e) {
    logger.warn('[CG Rounding] Failed to add rounding cuts:', e as any)
  }
  
  // Remove duplicate cuts
  const uniqueCuts = removeDuplicateCuts(cuts)
  
  logger.log(`Total cuts after deduplication: ${uniqueCuts.length}`)
  
  // Log statistics by type
  const cutsByType = {
    'gmi': uniqueCuts.filter(cut => cut.cgfType === 'gmi').length,
    'gomory': uniqueCuts.filter(cut => cut.cgfType === 'gomory').length,
    '2dsimplex': uniqueCuts.filter(cut => cut.cgfType === '2dsimplex').length
  }
  logger.log('Cuts by type after deduplication:', cutsByType)
  
  // Sort cuts by efficacy (descending)
  uniqueCuts.sort((a, b) => b.efficacy - a.efficacy)
  
  // --- Final, Global Stabilization Step ---
  // Proactively stabilize the coefficients of ALL generated cuts before returning.
  // This prevents numerical instability from any source (GMI, Gomory, etc.)
  const STABILIZATION_THRESHOLD = STABILITY.cutCoeffLimit // Max desired coefficient (6 digits)
  for (let i = 0; i < uniqueCuts.length; i++) {
    const cut = uniqueCuts[i]
    
    const allCoeffs = [...cut.alpha, cut.beta]
    const maxCoeff = Math.max(...allCoeffs.map(c => Math.abs(c)))

    if (maxCoeff > STABILIZATION_THRESHOLD) {
      logger.log(`[Global Cut Stabilization] Normalizing oversized cut. Max coeff: ${maxCoeff.toExponential(2)}`)
      const scalingFactor = maxCoeff / STABILIZATION_THRESHOLD

      // Scale down the entire inequality
      const newAlpha = cut.alpha.map(a => Math.round(a / scalingFactor))
      const newBeta = Math.round(cut.beta / scalingFactor)
      
      // Avoid creating a null cut (e.g. 0x + 0y <= 0)
      const isNullCut = newAlpha.every(a => a === 0) && newBeta === 0;
      if (!isNullCut) {
        uniqueCuts[i] = { ...cut, alpha: newAlpha, beta: newBeta }
      }
    }
  }

  return {
    cuts: uniqueCuts,
    tableau,
    fractionalRows: candidateRowIndices,
    cleanedProblem
  }
}

/**
 * Check if a point violates a cut
 */
export function checkCutViolation(cut: CanonicalCut, point: number[]): number {
  if (cut.alpha.length !== point.length) {
    throw new Error('Cut dimension does not match point dimension')
  }
  
  let lhs = 0
  for (let i = 0; i < cut.alpha.length; i++) {
    lhs += cut.alpha[i] * point[i]
  }
  
  // Violation is lhs - beta (positive means violated)
  return lhs - cut.beta
}

/**
 * Apply a cut to the problem by adding it as a new constraint
 */
export function applyCutToProblem(problem: IntegerProblem, cut: CanonicalCut): IntegerProblem {
  // Add the cut as a new constraint: alpha^T x <= beta
  const newA = [...problem.A, cut.alpha]
  const newB = [...problem.b, cut.beta]
  
  return {
    A: newA,
    b: newB,
    c: problem.c,
    inequalities: problem.inequalities
  }
}

/**
 * Get information about available cuts
 */
export function getCutInfo(problem: IntegerProblem): {
  hasFractionalSolution: boolean
  fractionalRowCount: number
  optimalValue: number
  optimalSolution: number[]
  fractionalRows: number[]
} {
  try {
    const { A_int, b_int } = ensureIntegerMatrix(problem.A, problem.b)
    const { candidateRowIndices, tableau } = getCandidateTableauRows(A_int, b_int, problem.c)
    
    // Extract optimal solution (first n components are original variables)
    const n = problem.c.length
    const optimalSolution = tableau.optimalSolution.slice(0, n).map((frac: Fraction) => frac.toNumber())
    
    return {
      hasFractionalSolution: candidateRowIndices.length > 0,
      fractionalRowCount: candidateRowIndices.length,
      optimalValue: tableau.objectiveValue.toNumber(),
      optimalSolution,
      fractionalRows: candidateRowIndices
    }
  } catch (error) {
    return {
      hasFractionalSolution: false,
      fractionalRowCount: 0,
      optimalValue: 0,
      optimalSolution: [],
      fractionalRows: []
    }
  }
}

/**
 * Evaluate objective function for a given point
 */
export function evaluateObjectiveFunction(point: number[], c: number[]): number {
  if (point.length !== c.length) {
    throw new Error('Point dimension must match objective coefficients dimension')
  }
  
  let value = 0
  for (let i = 0; i < point.length; i++) {
    value += c[i] * point[i]
  }
  
  return value
}

/**
 * Find the optimal integer programming value
 */
export function findIPOptimalValue(problem: IntegerProblem): number {
  // Find all integer points in the feasible region
  const box = computeAABBFromInequalities(problem.inequalities)
  const integerPoints = findIntegerPointsInPolyhedron(problem.inequalities, {
    xMin: box.xMin, xMax: box.xMax,
    yMin: box.yMin, yMax: box.yMax,
    zMin: box.zMin, zMax: box.zMax
  })
  
  if (integerPoints.length === 0) {
    return 0 // No feasible integer points
  }
  
  // Convert THREE.Vector3 to number arrays and evaluate objective function
  const objectiveValues = integerPoints.map(point => {
    const pointArray = [point.x, point.y, point.z]
    return evaluateObjectiveFunction(pointArray, problem.c)
  })
  
  // Find the maximum value (assuming maximization problem)
  return Math.max(...objectiveValues)
}

export function findIPOptimalSolution(problem: IntegerProblem): number[] {
  // Find all integer points in the feasible region
  const box = computeAABBFromInequalities(problem.inequalities)
  const integerPoints = findIntegerPointsInPolyhedron(problem.inequalities, {
    xMin: box.xMin, xMax: box.xMax,
    yMin: box.yMin, yMax: box.yMax,
    zMin: box.zMin, zMax: box.zMax
  })
  
  if (integerPoints.length === 0) {
    return [0, 0, 0] // No feasible integer points
  }
  
  // Convert THREE.Vector3 to number arrays and evaluate objective function
  const pointsWithValues = integerPoints.map(point => {
    const pointArray = [point.x, point.y, point.z]
    const objectiveValue = evaluateObjectiveFunction(pointArray, problem.c)
    return { point: pointArray, value: objectiveValue }
  })
  
  // Find the point with maximum objective value
  const optimalPoint = pointsWithValues.reduce((max, current) => 
    current.value > max.value ? current : max
  )
  
  return optimalPoint.point
}
